import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Check, X, Loader2, ShoppingCart, Sparkles, AlertCircle, Info, ExternalLink } from "lucide-react";
import { searchDomains, type DomainResult } from "@/lib/domains.functions";
import { toast } from "sonner";

const POPULAR_TLDS = [".com", ".net", ".io", ".sa", ".co", ".ai", ".app", ".dev"];

export function DomainSearch({
  embedded = false,
  initialQuery,
  autoSearch = false,
}: { embedded?: boolean; initialQuery?: string; autoSearch?: boolean } = {}) {
  const [q, setQ] = useState(initialQuery ?? "");
  const [cart, setCart] = useState<string[]>([]);
  const [details, setDetails] = useState<DomainResult | null>(null);
  const search = useServerFn(searchDomains);

  const mut = useMutation({
    mutationFn: async (query: string) => search({ data: { query } }),
    onError: (err: Error) => toast.error(err.message || "Search failed. Try again."),
  });

  const sanitize = (v: string) => v.toLowerCase().replace(/[^a-z0-9.-]/g, "").slice(0, 63);

  // Sync external query (e.g. when user picks an AI suggestion) and optionally auto-search.
  useEffect(() => {
    if (initialQuery === undefined) return;
    const v = sanitize(initialQuery);
    setQ(v);
    if (autoSearch && v.length >= 1) mut.mutate(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = sanitize(q);
    if (v.length < 1) return;
    mut.mutate(v);
  };

  const toggleCart = (d: string) => {
    setCart((c) => (c.includes(d) ? c.filter((x) => x !== d) : [...c, d]));
  };

  const cartTotal = useMemo(() => {
    const items = mut.data?.results.filter((r) => cart.includes(r.domain)) ?? [];
    return items.reduce((s, r) => s + (r.price ?? 0), 0);
  }, [cart, mut.data]);

  const alternatives = useMemo(() => {
    if (!details || !mut.data) return [];
    return mut.data.results
      .filter((r) => r.domain !== details.domain && r.available === true)
      .slice(0, 6);
  }, [details, mut.data]);

  return (
    <section className={embedded ? "" : "py-24"}>
      <div className={embedded ? "" : "mx-auto max-w-5xl px-4 lg:px-8"}>
        <div className={`relative overflow-hidden rounded-3xl border border-border bg-card shadow-card ${embedded ? "p-5 sm:p-6" : "p-8 sm:p-12"}`}>
          <div className="absolute inset-0 -z-10 bg-hero-glow opacity-60" />

          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-widest text-primary">Domains & hosting</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
              Find your <span className="text-gradient">perfect domain</span>
            </h2>
            <p className="mt-3 text-muted-foreground">
              Live availability across 12+ TLDs. Register, transfer and manage in one place.
            </p>
          </div>

          <form onSubmit={onSubmit} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(sanitize(e.target.value))}
                placeholder="yourbrand or yourbrand.com"
                className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                aria-label="Domain search"
              />
            </div>
            <button
              type="submit"
              disabled={mut.isPending || q.trim().length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-white shadow-glow disabled:opacity-60"
            >
              {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {mut.isPending ? "Checking..." : "Search"}
            </button>
          </form>

          {/* Popular TLD quick-prices when idle */}
          {!mut.data && !mut.isPending && (
            <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2 max-w-3xl mx-auto">
              {POPULAR_TLDS.map((t) => (
                <div key={t} className="rounded-md border border-border bg-background/50 p-3 text-center">
                  <div className="text-sm font-semibold">{t}</div>
                  <div className="text-xs text-muted-foreground">
                    {priceFor(t)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Results */}
          <AnimatePresence mode="wait">
            {mut.isPending && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="mt-8 space-y-2 max-w-3xl mx-auto"
              >
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-14 rounded-lg border border-border bg-background/40 relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                ))}
              </motion.div>
            )}

            {mut.data && !mut.isPending && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mt-8 max-w-3xl mx-auto"
              >
                <div className="space-y-2">
                  {mut.data.results.map((r, i) => (
                    <ResultRow
                      key={r.domain}
                      r={r}
                      delay={i * 0.03}
                      inCart={cart.includes(r.domain)}
                      onToggle={() => toggleCart(r.domain)}
                      onDetails={() => setDetails(r)}
                    />
                  ))}
                </div>

                {cart.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-xl border border-primary/40 bg-gradient-brand/10 p-4"
                  >
                    <div className="text-sm">
                      <span className="font-semibold">{cart.length}</span> domain{cart.length > 1 ? "s" : ""} selected
                      <span className="text-muted-foreground"> · {cart.join(", ")}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Total · </span>
                        <span className="font-bold text-gradient">${cartTotal.toFixed(2)}</span>
                        <span className="text-xs text-muted-foreground">/yr</span>
                      </div>
                      <button
                        onClick={() => toast.success(`Continuing to checkout for ${cart.length} domain${cart.length > 1 ? "s" : ""}`)}
                        className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-4 py-2 text-sm font-semibold text-white shadow-glow"
                      >
                        <ShoppingCart className="h-4 w-4" /> Checkout
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {mut.isError && (
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" /> Something went wrong. Please try again.
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <DetailsModal
        result={details}
        alternatives={alternatives}
        inCart={(d) => cart.includes(d)}
        onToggleCart={toggleCart}
        onClose={() => setDetails(null)}
      />
    </section>
  );
}

function ResultRow({ r, delay, inCart, onToggle, onDetails }: { r: DomainResult; delay: number; inCart: boolean; onToggle: () => void; onDetails: () => void }) {
  const isAvail = r.available === true;
  const isTaken = r.available === false;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay }}
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border p-4 transition ${
        isAvail
          ? "border-primary/30 bg-background hover:border-primary/60"
          : "border-border bg-background/50"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
            isAvail ? "bg-emerald-500/15 text-emerald-500" : isTaken ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground"
          }`}
        >
          {isAvail ? <Check className="h-4 w-4" /> : isTaken ? <X className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onDetails} className="font-display text-base font-semibold truncate hover:text-primary transition text-left">
              {r.domain}
            </button>
            {r.premium && (
              <button
                onClick={onDetails}
                className="inline-flex items-center gap-1 rounded-full bg-gradient-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white"
                aria-label="Premium domain details"
              >
                <Sparkles className="h-3 w-3" /> Premium
              </button>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {isAvail
              ? "Available — register now"
              : isTaken
              ? "Already registered — view details for WHOIS & alternatives"
              : "Availability check unavailable for this TLD"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:justify-end">
        {r.price !== undefined && (
          <div className="text-right">
            <div className={`text-base font-bold ${isAvail ? "text-gradient" : "text-muted-foreground line-through"}`}>
              ${r.price.toFixed(2)}<span className="text-xs font-normal text-muted-foreground">/yr</span>
            </div>
            {r.renew && isAvail && (
              <div className="text-[10px] text-muted-foreground">renews ${r.renew.toFixed(2)}/yr</div>
            )}
          </div>
        )}
        <button
          onClick={onDetails}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-2 text-xs font-semibold hover:bg-secondary transition"
          aria-label={`View details for ${r.domain}`}
        >
          <Info className="h-3.5 w-3.5" /> Details
        </button>
        {isAvail ? (
          <button
            onClick={onToggle}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition ${
              inCart
                ? "border border-primary bg-primary/10 text-primary"
                : "bg-gradient-brand text-white shadow-glow hover:opacity-95"
            }`}
          >
            {inCart ? <><Check className="h-3.5 w-3.5" /> Added</> : <><ShoppingCart className="h-3.5 w-3.5" /> Add</>}
          </button>
        ) : null}
      </div>
    </motion.div>
  );
}

function DetailsModal({
  result,
  alternatives,
  inCart,
  onToggleCart,
  onClose,
}: {
  result: DomainResult | null;
  alternatives: DomainResult[];
  inCart: (d: string) => boolean;
  onToggleCart: (d: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!result) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [result, onClose]);

  return (
    <AnimatePresence>
      {result && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="domain-details-title"
        >
          <div className="absolute inset-0 bg-background/70 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="relative w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl border border-border bg-card shadow-card overflow-hidden"
          >
            <div className="relative p-6 border-b border-border bg-hero-glow">
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background/60 hover:bg-secondary transition"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="text-xs font-semibold uppercase tracking-widest text-primary">Domain details</div>
              <h3 id="domain-details-title" className="mt-2 font-display text-2xl font-bold break-all">
                {result.domain}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <StatusBadge available={result.available} />
                {result.premium && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gradient-brand px-2 py-0.5 font-bold uppercase tracking-widest text-white">
                    <Sparkles className="h-3 w-3" /> Premium
                  </span>
                )}
                <span className="rounded-full border border-border bg-background/50 px-2 py-0.5 text-muted-foreground">
                  TLD {result.tld}
                </span>
              </div>
            </div>

            <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Pricing */}
              {result.price !== undefined && (
                <div className="flex items-end justify-between rounded-lg border border-border bg-background/50 p-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Registration</div>
                    <div className="text-2xl font-bold text-gradient">
                      ${result.price.toFixed(2)}<span className="text-xs font-normal text-muted-foreground">/yr</span>
                    </div>
                  </div>
                  {result.renew && (
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Renewal</div>
                      <div className="text-sm font-semibold">${result.renew.toFixed(2)}<span className="text-xs font-normal text-muted-foreground">/yr</span></div>
                    </div>
                  )}
                </div>
              )}

              {/* Premium explanation */}
              {result.premium && (
                <div className="rounded-lg border border-primary/30 bg-gradient-brand/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Sparkles className="h-4 w-4 text-primary" /> Why is this premium?
                  </div>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    Short, memorable names (3 characters or fewer) are rare and high-demand. Registries
                    price them above the standard tier. The renewal rate stays at the regular price once
                    you own it.
                  </p>
                </div>
              )}

              {/* WHOIS / RDAP */}
              <div className="rounded-lg border border-border bg-background/50 p-4">
                <div className="text-sm font-semibold">Registration lookup</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Inspect the public WHOIS / RDAP record to see the registrar, registration date and
                  expiration for this domain.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href={`https://rdap.org/domain/${result.domain}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-secondary transition"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> WHOIS / RDAP
                  </a>
                  <a
                    href={`https://www.whois.com/whois/${result.domain}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-secondary transition"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> whois.com
                  </a>
                  <a
                    href={`https://${result.domain}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-secondary transition"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Visit site
                  </a>
                </div>
              </div>

              {/* Alternatives */}
              {result.available === false && (
                <div>
                  <div className="text-sm font-semibold">Suggested alternatives</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    These available extensions match your search and are ready to register.
                  </p>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {alternatives.length === 0 && (
                      <div className="text-xs text-muted-foreground italic">
                        No available alternatives in this search. Try a different name above.
                      </div>
                    )}
                    {alternatives.map((alt) => {
                      const added = inCart(alt.domain);
                      return (
                        <div
                          key={alt.domain}
                          className="flex items-center justify-between gap-2 rounded-md border border-primary/20 bg-background p-3"
                        >
                          <div className="min-w-0">
                            <div className="font-display text-sm font-semibold truncate">{alt.domain}</div>
                            <div className="text-[11px] text-emerald-500">
                              Available · ${alt.price?.toFixed(2)}/yr
                            </div>
                          </div>
                          <button
                            onClick={() => onToggleCart(alt.domain)}
                            className={`shrink-0 inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition ${
                              added
                                ? "border border-primary bg-primary/10 text-primary"
                                : "bg-gradient-brand text-white shadow-glow"
                            }`}
                          >
                            {added ? <><Check className="h-3 w-3" /> Added</> : <><ShoppingCart className="h-3 w-3" /> Add</>}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer action */}
            <div className="flex items-center justify-between gap-3 border-t border-border bg-background/40 p-4">
              <button
                onClick={onClose}
                className="text-sm font-semibold text-muted-foreground hover:text-foreground transition"
              >
                Close
              </button>
              {result.available === true ? (
                <button
                  onClick={() => {
                    onToggleCart(result.domain);
                    onClose();
                  }}
                  className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-4 py-2 text-sm font-semibold text-white shadow-glow"
                >
                  {inCart(result.domain) ? <><Check className="h-4 w-4" /> In cart</> : <><ShoppingCart className="h-4 w-4" /> Add to cart</>}
                </button>
              ) : (
                <a
                  href={`https://rdap.org/domain/${result.domain}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-4 py-2 text-sm font-semibold text-white shadow-glow"
                >
                  <ExternalLink className="h-4 w-4" /> Open WHOIS
                </a>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatusBadge({ available }: { available: boolean | null }) {
  if (available === true)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-500">
        <Check className="h-3 w-3" /> Available
      </span>
    );
  if (available === false)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 font-semibold text-destructive">
        <X className="h-3 w-3" /> Taken
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-semibold text-muted-foreground">
      <AlertCircle className="h-3 w-3" /> Unknown
    </span>
  );
}

function priceFor(tld: string) {
  const map: Record<string, string> = {
    ".com": "$9.99/yr",  ".net": "$11.99/yr", ".io": "$32.00/yr",
    ".sa":  "$24.00/yr", ".co":  "$24.99/yr", ".ai": "$69.00/yr",
    ".app": "$14.99/yr", ".dev": "$12.99/yr",
  };
  return map[tld] ?? "—";
}
